import { Button, Spin, Message, Input, Modal, Pagination } from "../../../../BP2Components";
import React, { memo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import EditableTable from "./DedicatedList";
import { getList, getProperlyFilteredList } from "../../../../../services/api/Api";
import IntlMessages from "@util/IntlMessages";
import "./DedicatedListTable.less";
import { SearchOutlined } from "@ant-design/icons";
import { isActivePublicUser } from "../../../../../util/Scripts";
import { Icon_Plus2 } from "../../../../BP2Components/Icons/icons";
import FormComplete from "../../../FormComplete";

const DedicatedListTable = memo(
   ({ listId, getValueFromAdvancedSearch, closeModal, fieldTechname, reloadFieldOptions }) => {
      const intl = useIntl();
      const [columns, setColumns] = useState([]);
      const [rows, setRows] = useState([]);
      const [page, setPage] = useState(1);
      const [metaData, setMetaData] = useState();
      const [loading, setLoading] = useState(true);
      const [loadingPage, setLoadingPage] = useState(false);
      const [filters, setFilters] = useState([]);
      const [isOpen, setIsOpen] = useState(false);
      const [isNewItemAdded, setIsNewItemAdded] = useState(false);

      const handleOption = (value) => (_e) => {
         try {
            getValueFromAdvancedSearch(value);
            closeModal(false);
         } catch (error) {
            console.log(error);
            Message.error(intl.formatMessage({ id: "dedicatedList.errorChoosingOption" }));
         }
      };

      const handlePageChange = (currentPage) => {
         setPage(currentPage);
         setLoadingPage(true);
      };

      //Filtering
      const handleColumnFilter = (columnTechname) => ({
         filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: "1rem" }}>
               <Input
                  type="text"
                  value={selectedKeys?.[0]}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={handleFilter(columnTechname, selectedKeys?.[0], confirm)}
               />
               <Button
                  buttonType="primary"
                  onClick={handleFilter(columnTechname, selectedKeys?.[0], confirm)}
               >
                  <IntlMessages id="filter.search" />
               </Button>
               <Button
                  buttonType="primary"
                  onClick={handleFilterReset(columnTechname, setSelectedKeys, clearFilters)}
               >
                  <IntlMessages id="filter.reset" />
               </Button>
            </div>
         ),
         filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "var(--second)" : "" }} />
         ),
      });

      const handleFilterReset = (columnTechname, setSelectedKeys, clearFilters) => (_e) => {
         setFilters((prevFilters) =>
            prevFilters.filter((filter) => filter.columnTechname !== columnTechname),
         );
         setSelectedKeys([]);
         clearFilters();
      };

      const handleFilter = (columnTechname, value, confirm) => (_e) => {
         setFilters((prevFilters) => {
            // If value is empty, we delete filter completely
            if (!value) {
               return prevFilters.filter((filter) => filter.columnTechname !== columnTechname);
               // If columnTechname cannot be found in filters, we add new one
            } else if (!prevFilters.find((filter) => filter.columnTechname === columnTechname)) {
               return [...prevFilters, { columnTechname, value }];
               // Else we edit the filter
            } else {
               return prevFilters.map((filter) => {
                  if (filter.columnTechname === columnTechname && filter.value !== value) {
                     return { columnTechname, value };
                  } else {
                     return filter;
                  }
               });
            }
         });
         confirm();
      };

      const handleListResponse = (res) => {
         const columnsCopy = [];
         const responseData = isActivePublicUser() ? res?.data?.getPublicList : res?.data?.default;
         responseData.columns.forEach((column) => {
            if (
               (column.options.visible == "1" || column.options.resolver) &&
               column.options.techName
            ) {
               columnsCopy.push({
                  title: column?.label,
                  dataIndex: column?.label,
                  techName: column?.options?.techName,
                  precision: column?.options?.precision,
                  key: column?.options?.techName,
                  ...handleColumnFilter(column?.options?.techName),
               });
            }
         });

         columnsCopy.unshift({
            title:
               responseData?.meta?.addItemFormId && responseData?.meta?.addItemFormId != 0 ? (
                  <Button
                     buttonType="primary"
                     size="regular"
                     style={{
                        border: "none",
                        position: "relative",
                        width: 32,
                        display: "flex",
                        padding: 0,
                     }}
                     className="add-record-btn"
                     onClick={() => setIsOpen(true)}
                  >
                     <Icon_Plus2 fill="var(--btn-primaryText)" />
                  </Button>
               ) : (
                  <IntlMessages id="global.actions" />
               ),
            dataIndex: "actions",
            key: "actions",
            render: (_text, row) => (
               <Button
                  htmlType="button"
                  size="regular"
                  style={{ marginBottom: 0 }}
                  buttonType="primary"
                  onClick={handleOption(row[columnsCopy[1]?.dataIndex])}
               >
                  <IntlMessages id="global.choose" />
               </Button>
            ),
         });
         /* columnsCopy.push({
            title: <button onClick={setIsOpen}>+</button>,
            dataIndex: "addRecord",
            key: "addRecord",
            render: (_text, row) => <></>
         }); */

         setMetaData(responseData?.meta);

         let rowsTable = responseData.data.map((row, index) => {
            const rowObj = {};
            columnsCopy.forEach((column) => {
               if (typeof row[column.dataIndex] === "string") {
                  let rowValue = Number(row[column.dataIndex]);
                  // This has to check if rowValue is an empty string or not, because Number() converts empty string to 0
                  if (
                     row[column.dataIndex] &&
                     column.precision &&
                     row[column.dataIndex].length > 0 &&
                     !isNaN(rowValue)
                  ) {
                     rowObj[column.dataIndex] = rowValue.toFixed(column.precision).toString();
                  } else {
                     rowObj[column.dataIndex] = row[column.dataIndex];
                  }
               } else {
                  rowObj[column.dataIndex] = row[column.dataIndex];
               }
            });
            row.key = index;
            return rowObj;
         });

         // This makes sure that table has spinner inside only if we change pages, not on the initial render
         if (rows?.length > 0) {
            setRows(rowsTable);
            setLoadingPage(false);
         } else {
            setColumns(columnsCopy);
            setRows(rowsTable);
            setLoading(false);
         }
      };

      useEffect(() => {
         // If there was already some data, we show loader
         if (rows?.length > 0) {
            setLoadingPage(true);
         }

         //TODO: Implement when backend finishes endpoint for filtering list via techname
         if (filters.length > 0) {
            getProperlyFilteredList(listId, page, 40, filters)
               .then((res) => {
                  handleListResponse(res);
                  setIsNewItemAdded(false);
               })
               .catch(() =>
                  message.error(
                     intl.formatMessage({ id: "forms.errorWhileFetchingDataForListTable" }),
                  ),
               );
            return;
         }
         getList(listId, page, 40)
            .then((res) => {
               handleListResponse(res);
               setIsNewItemAdded(false);
            })
            .catch(() =>
               Message.error(
                  intl.formatMessage({ id: "forms.errorWhileFetchingDataForListTable" }),
               ),
            );
      }, [listId, page, filters, isNewItemAdded]);

      return loading ? (
         <div className="spin-middle">
            <Spin />
         </div>
      ) : (
         <section className="dedicated-list-table add-record-button-true">
            {/* {metaData.addItemFormId && metaData.addItemFormId != 0 && (
               <Button
                  buttonType="primary"
                  className="dedicated-list-table__add-new-item-btn"
                  style={{ border: "none", position: "relative" }}
                  onClick={setIsOpen}
               >
                  <IntlMessages id="form.addNewItem" />
               </Button>
            )} */}
            {isOpen && (
               <Modal
                  isOpen={isOpen}
                  onCancel={() => setIsOpen(false)}
                  topBarStyles={{ border: "none", padding: "10px" }}
                  width={1000}
                  height={700}
                  mainStyles={{ borderRadius: "15px", resize: "none" }}
                  footerContent={
                     <Button
                        className="dedicated-list-table__cancel-btn"
                        buttonType="third"
                        onClick={() => setIsOpen(false)}
                     >
                        <IntlMessages id="Cancel" />
                     </Button>
                  }
               >
                  <FormComplete
                     formId={metaData?.addItemFormId}
                     saveCallback={(dataId, res) => {
                        const newRecordId = res?.data?.default?.recordInfo?.[0]?.id;
                        setIsOpen();
                        setIsNewItemAdded(true);
                        setTimeout(() => {
                           closeModal();
                        }, 2000);
                        reloadFieldOptions(fieldTechname, newRecordId);
                        Message.destroy();
                        Message.success(
                           intl.formatMessage({ id: "addRecordBtn.newOptionHasBeenAdded" }),
                           5000,
                        );
                     }}
                  />
               </Modal>
            )}
            <EditableTable columns={columns} rows={rows} loadingPage={loadingPage} />
            {metaData?.totalPages > 1 && (
               <div className="dedicated-list-table__pagination">
                  <Pagination
                     initialPage={1}
                     totalItems={metaData.totalItemsCount}
                     defaultPageSize={10}
                     onChange={handlePageChange}
                  />
               </div>
            )}
         </section>
      );
   },
);

DedicatedListTable.propTypes = {
   listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
   form: PropTypes.any.isRequired,
   fieldTechname: PropTypes.string.isRequired,
};

export default DedicatedListTable;
